import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyBlocks, unblockUser, unmuteUser } from '../../services/api/userService';

const nameOf = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || 'Player';

/**
 * "Blocked and muted" players of the signed-in user, with Unblock / Unmute.
 * Shown in Profile → Account Settings.
 */
function BlockedUsersList() {
  const [lists, setLists] = useState({ blocked: [], muted: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getMyBlocks();
      setLists({ blocked: res?.data?.blocked || [], muted: res?.data?.muted || [] });
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load your blocked and muted players.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (kind, user) => {
    const key = `${kind}-${user.id}`;
    try {
      setBusyKey(key);
      setError('');
      await (kind === 'blocked' ? unblockUser(user.id) : unmuteUser(user.id));
      setLists((prev) => ({ ...prev, [kind]: prev[kind].filter((u) => u.id !== user.id) }));
    } catch (err) {
      setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusyKey('');
    }
  };

  const section = (kind, title, actionLabel, emptyText) => (
    <div className="blocked-list__section" data-testid={`blocked-list-${kind}`}>
      <h4 style={{ margin: '0.75rem 0 0.4rem', color: '#fff' }}>{title}</h4>
      {lists[kind].length === 0 ? (
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', margin: 0 }}>{emptyText}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {lists[kind].map((u) => (
            <li
              key={u.id}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0.4rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
            >
              <Link to={`/players/${u.short_code || u.uuid || u.id}`} style={{ color: '#e0e0e0' }}>
                {nameOf(u)}{u.username ? <span style={{ color: '#b8acff' }}> @{u.username}</span> : null}
              </Link>
              <button
                type="button"
                className="upgrade-cancel"
                style={{ padding: '0.3rem 0.8rem' }}
                onClick={() => remove(kind, u)}
                disabled={busyKey === `${kind}-${u.id}`}
                aria-label={`${actionLabel} ${nameOf(u)}`}
              >
                {busyKey === `${kind}-${u.id}` ? '...' : actionLabel}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  if (loading) {
    return <p style={{ color: 'rgba(255,255,255,0.7)' }}>Loading…</p>;
  }

  return (
    <div className="blocked-list">
      {error && <p role="alert" style={{ color: '#f87171', fontSize: '0.85rem' }}>{error}</p>}
      {section('blocked', 'Blocked', 'Unblock', "You haven't blocked anyone.")}
      {section('muted', 'Muted', 'Unmute', "You haven't muted anyone.")}
    </div>
  );
}

export default BlockedUsersList;
